import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { getQuerystringValue, notifyError } from "utils";
import { isNonEmptyArray, isNonEmptyString, isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";
import Button from "@cayuse/shared-react.components.button";
import Modal from "@cayuse/shared-react.components.modal";
import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";
import TextArea from "@cayuse/shared-react.components.form.inputs.text-area";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";

import styles from "./styles.css";


const Certification = ({
	completeCertificationTask,
	dictionary,
	formInstance,
	getInstance,
	instanceId,
	instanceTasks,
	isCertifiable,
	certificationTextData,
	filterInstanceCertificationTasks
}) => {
	const [ isLoading, setIsLoading ] = useState(false);
	const { search } = useLocation();
	const [ preselectedTaskId, setPreselectedTaskId ] = useState(null);
	const [ isModalOpen, setIsModalOpen ] = useState(false);
	const [ selectedCertification, setSelectedCertification ] = useState(null);
	const [ comment, setComment ] = useState("");
	const [ confirmed, setConfirmed ] = useState(false);
	const [ disabledReason, setDisabledReason ] = useState(null);
	const [ certificationTasks, setCertificationTasks ] = useState([]);

	const resetData = () => {
		setConfirmed(false);
		setComment("");
	};

	const onModalClose = () => {
		setIsModalOpen(false);
		resetData();
	};

	const handleSave = () => {
		setIsLoading(true);
		if (selectedCertification) {
			const taskId = selectedCertification.id;

			completeCertificationTask(
				taskId,
				instanceId,
				selectedCertification.taskType,
				selectedCertification.targetName,
				comment
			)
				.then(() => {
					onModalClose();
					setSelectedCertification(null);
					getInstance(instanceId);
				})
				.catch((e) => {
					const message = dictionary.errors.unableToSave || "Unable to complete the certification";
					notifyError(message, e);
				})
				.finally(() => {
					setIsLoading(false);
				});
		} else {
			setIsLoading(false);
		}
	};

	const handleCancel = () => {
		onModalClose();
	};

	const toggleConfirmed = value => {
		setConfirmed(value);
	};

	const onCommentChange = value => {
		setComment(value);
	};

	useEffect(() => {
		if (isNonEmptyObject(formInstance) && isNonEmptyArray(instanceTasks)) {
			const certificationTasks = filterInstanceCertificationTasks(formInstance, instanceTasks);

			if (isNonEmptyArray(certificationTasks) && certificationTasks.length > 0) {
				setSelectedCertification(certificationTasks[0]);
			} else {
				setSelectedCertification(null);
			}

			setCertificationTasks(certificationTasks);
		} else {
			setCertificationTasks([]);
			setSelectedCertification(null);
		}
	}, [ formInstance, instanceTasks ]);

	useEffect(() => {
		const queryStringTaskId = getQuerystringValue("task");

		if (!preselectedTaskId || preselectedTaskId !== queryStringTaskId) {
			const preselectedTask = isNonEmptyString(queryStringTaskId)
				? certificationTasks.find(({ id }) => id === queryStringTaskId)
				: undefined;

			if (preselectedTask) {
				setPreselectedTaskId(queryStringTaskId);
				setSelectedCertification(preselectedTask);
				setIsModalOpen(true);
			}
		}
	}, [ certificationTasks, preselectedTaskId, instanceId, search ]);


	useEffect(() => {
		if (confirmed === false) {
			setDisabledReason(dictionary.errors?.notConfirmed || "You need to confirm the certification");
		} else {
			setDisabledReason(null);
		}
	}, [ confirmed, dictionary.errors ]);


	return selectedCertification
		? (
			<div data-component="Certification">
				<Button primary
				        onClick={() => setIsModalOpen(true)}
				        visible={selectedCertification}
				        disabled={!isCertifiable}
				>
					{dictionary.buttonLabel }
				</Button>
				<Modal ariaLabel={dictionary.modalTitle} closeIcon open={isModalOpen} onClose={onModalClose}>
					<Modal.Header className="ui header whiteOnBlue">
						<h2>{dictionary.modalTitle}</h2>
					</Modal.Header>

					<Modal.Content className="modalContent">
						<div className={`ui ${styles.fieldset}`}
							 dangerouslySetInnerHTML={{ __html: certificationTextData?.text }}
						/>
						<div className={"ui form"}>
							<Checkbox
								id="confirmCertification"
								onClick={() => toggleConfirmed(!confirmed)}
								checked={confirmed}
								label={dictionary.fields?.confirmPlaceholder || "Confirm Certification."}
							/>
						</div>
						<div className={`ui input ${styles.fieldset}`}>
							<label>
								{dictionary.fields?.comment || "Comment"}
							</label>
						</div>
						<div className={"ui form"}>
							<TextArea
								value={comment}
								placeholder={dictionary.commentPlaceholder || "Please add a comment."}
								onChange={(e, { value }) => onCommentChange(value)}
							/>
						</div>
					</Modal.Content>

					<VerticalSpacing/>

					<Modal.Actions>
						<div className={styles.buttonGroup}>
							<Button secondary onClick={handleCancel}>
								{dictionary.cancelLabel || "Cancel"}
							</Button>

							<Button primary
							        onClick={handleSave}
							        disabled={isNonEmptyString(disabledReason) || isLoading}
							        busy={isLoading}
							        loadingMessage="Save in process"
							        tooltip={disabledReason}
							>
								{dictionary.saveLabel || "Save"}
							</Button>
						</div>
					</Modal.Actions>
				</Modal>
			</div>
		)
		: null;
};

Certification.propTypes = {
	onCancel: PropTypes.func,
	onSave: PropTypes.func,
	tasks: PropTypes.arrayOf(PropTypes.object)
};

export default Certification;
